import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import ForceGraph2D, {type NodeObject, type LinkObject} from "react-force-graph-2d";
import {
  Search,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Info,
  ExternalLink,
  X,
  MessageCircle,
  Loader2,
  Wand2,
  User,
  Star,
} from "lucide-react";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {ScrollArea} from "@/components/ui/scroll-area";
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group";
import {useCreateFavorite, useFavorites} from "@/hooks/use-favorites";
import {
  fetchInfluenceGraph,
  type DataSource,
  type InfluenceGraphResponse,
} from "@/lib/influence-api";
import {SiInstagram, SiX} from "@icons-pack/react-simple-icons";

function getProfileUrl(username: string, source: DataSource) {
  if (source === "x") {
    return `https://x.com/${username}`;
  }
  return `https://www.instagram.com/${username}`;
}

// Page-level types that extend react-force-graph types
interface GraphNode extends NodeObject {
  id: string;
  username: string;
  influenceCount: number;
  influencedBy: number;
  commentsMade: number;
  color?: string;
  size?: number;
  profile_image?: string;
  biography?: string;
  followers?: number;
  following?: number;
  posts_count?: number;
}

interface InfluenceComment {
  postUrl: string;
  parentUrl: string;
  comment: string;
  date: string;
  likes: number;
  commenter: string;
}

interface GraphLink extends LinkObject {
  source: string | GraphNode;
  target: string | GraphNode;
  value: number;
  comments: InfluenceComment[];
}

interface NodeWithCoords extends GraphNode {
  x?: number;
  y?: number;
}

function transformApiData(apiData: InfluenceGraphResponse): {
  nodes: GraphNode[];
  links: GraphLink[];
} {
  const maxInfluence = Math.max(...apiData.nodes.map((n) => n.influence_count), 1);

  const nodes: GraphNode[] = apiData.nodes.map((node) => {
    const influenceRatio = node.influence_count / maxInfluence;
    const lightness = 45 + influenceRatio * 25;
    const baseSize = 6;
    const maxSize = 40;

    return {
      id: node.id,
      username: node.username,
      influenceCount: node.influence_count,
      influencedBy: node.influenced_by,
      commentsMade: node.comments_made,
      color: `hsl(0, 0%, ${lightness}%)`,
      size: baseSize + (node.influence_count / maxInfluence) * (maxSize - baseSize),
      profile_image: node.profile_image ?? undefined,
      biography: node.biography ?? undefined,
      followers: node.followers ?? undefined,
      following: node.following ?? undefined,
      posts_count: node.posts_count ?? undefined,
    };
  });

  const links: GraphLink[] = apiData.links.map((link) => ({
    source: link.source,
    target: link.target,
    value: link.value,
    comments: (link.comments ?? []).map((c) => ({
      postUrl: c.post_url,
      parentUrl: c.parent_url,
      comment: c.comment,
      date: c.date,
      likes: c.likes ?? 0,
      commenter: c.commenter,
    })),
  }));

  return {nodes, links};
}

const imgCache = new Map<string, HTMLImageElement>();

export function InfluenceMapPage() {
  const graphRef = useRef<typeof ForceGraph2D | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const urlAccountProcessed = useRef(false);
  const forcesConfigured = useRef(false);
  const [dimensions, setDimensions] = useState({width: 0, height: 0});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedLink, setSelectedLink] = useState<GraphLink | null>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params for platform and account selection
  const platformParam = searchParams.get("platform");
  const accountIdParam = searchParams.get("account_id");
  const [dataSource, setDataSource] = useState<DataSource>(
    platformParam === "x" ? "x" : platformParam === "instagram" ? "instagram" : "instagram"
  );

  // Update URL when platform changes
  const handleDataSourceChange = useCallback(
    (newSource: DataSource) => {
      setDataSource(newSource);
      setSelectedNode(null);
      setSelectedLink(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("platform", newSource);
        next.delete("account_id");
        return next;
      });
    },
    [setSearchParams]
  );

  // Favorites
  const createFavoriteMutation = useCreateFavorite();
  const {data: favoritesData} = useFavorites("account");

  const {
    data: apiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["influence-graph", dataSource],
    queryFn: () => fetchInfluenceGraph(dataSource),
    staleTime: 5 * 60 * 1000,
  });

  const graphData = useMemo(() => {
    if (!apiData) return {nodes: [], links: []};
    return transformApiData(apiData);
  }, [apiData]);

  // Auto-select account from URL params after data loads (runs once on mount)
  useEffect(() => {
    if (urlAccountProcessed.current) return;
    if (!accountIdParam || !graphData.nodes.length) return;

    const node = graphData.nodes.find(
      (n) => n.id === accountIdParam || n.username === accountIdParam
    );
    if (node) {
      urlAccountProcessed.current = true;
      // Use setTimeout to avoid cascading renders
      setTimeout(() => {
        setSelectedNode(node);
        // Center on the node after graph settles
        setTimeout(() => {
          const fg = graphRef.current as unknown as {
            centerAt: (x: number, y: number, ms?: number) => void;
            zoom: (k: number, ms?: number) => void;
          };
          const nodeWithCoords = node as NodeWithCoords;
          if (fg?.centerAt && nodeWithCoords.x !== undefined && nodeWithCoords.y !== undefined) {
            fg.centerAt(nodeWithCoords.x, nodeWithCoords.y, 400);
            fg.zoom(2, 400);
          }
        }, 600);
      }, 0);
    }
  }, [accountIdParam, graphData.nodes]);

  // Check if selected node is already favorited
  const isSelectedNodeFavorited = useMemo(() => {
    if (!selectedNode || !favoritesData?.favorites) return false;
    const entityId = `${dataSource}:${selectedNode.id}`;
    return favoritesData.favorites.some((f) => f.entity_id === entityId);
  }, [selectedNode, favoritesData, dataSource]);

  const handleAddToFavorites = useCallback(() => {
    if (!selectedNode) return;
    createFavoriteMutation.mutate({
      entity_type: "account",
      entity_id: `${dataSource}:${selectedNode.id}`,
    });
  }, [selectedNode, dataSource, createFavoriteMutation]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return graphData;

    const term = searchTerm.toLowerCase();
    const matchingNodes = graphData.nodes.filter((n) => n.username.toLowerCase().includes(term));
    const matchingIds = new Set(matchingNodes.map((n) => n.id));

    graphData.links.forEach((link) => {
      if (matchingIds.has(link.source as string)) {
        const targetNode = graphData.nodes.find((n) => n.id === link.target);
        if (targetNode) matchingIds.add(targetNode.id);
      }
      if (matchingIds.has(link.target as string)) {
        const sourceNode = graphData.nodes.find((n) => n.id === link.source);
        if (sourceNode) matchingIds.add(sourceNode.id);
      }
    });

    const filteredNodes = graphData.nodes.filter((n) => matchingIds.has(n.id));
    const filteredLinks = graphData.links.filter(
      (l) => matchingIds.has(l.source as string) && matchingIds.has(l.target as string)
    );

    return {nodes: filteredNodes, links: filteredLinks};
  }, [graphData, searchTerm]);

  const selectedNodeLinks = useMemo(() => {
    if (!selectedNode) return [];
    return graphData.links.filter(
      (l) =>
        (l.source as string) === selectedNode.id ||
        (typeof l.source === "object" && (l.source as GraphNode).id === selectedNode.id) ||
        (l.target as string) === selectedNode.id ||
        (typeof l.target === "object" && (l.target as GraphNode).id === selectedNode.id)
    );
  }, [selectedNode, graphData.links]);

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const {width, height} = containerRef.current.getBoundingClientRect();

      // Only update if dimensions actually changed and are valid
      if (width > 0 && height > 0) {
        setDimensions((prev) => {
          if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
            return prev;
          }
          return {width, height};
        });
      }
    };

    // Immediate check
    measure();

    // Poll every 200ms until component unmounts - guarantees we catch the size eventually
    const intervalId = setInterval(measure, 200);

    const resizeObserver = new ResizeObserver(measure);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearInterval(intervalId);
      resizeObserver.disconnect();
    };
  }, []);

  // Configure forces when graph mounts or data changes
  const configureAndReheat = useCallback(() => {
    const fg = graphRef.current as unknown as {
      zoomToFit: (ms?: number, padding?: number) => void;
      d3Force: (forceName: string, forceFn?: unknown) => unknown;
      d3ReheatSimulation: () => void;
    };
    if (!fg?.d3Force) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const charge = fg.d3Force("charge") as any;
    if (charge) {
      charge.strength(-150); // Gentler repulsion for calmer animation
      charge.distanceMax(400);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (fg.d3Force("link") as any)?.distance(80);

    fg.d3ReheatSimulation?.();
  }, []);

  // Reset forcesConfigured when dataSource changes
  useEffect(() => {
    forcesConfigured.current = false;
  }, [dataSource]);

  useEffect(() => {
    if (!apiData || forcesConfigured.current) return;

    // Configure forces after graph initialization (only once per dataSource)
    const t1 = setTimeout(() => {
      if (!graphRef.current || forcesConfigured.current) return;
      forcesConfigured.current = true;
      configureAndReheat();
      // Only zoomToFit if no account is selected from URL
      if (!accountIdParam) {
        const fg = graphRef.current as unknown as {
          zoomToFit: (ms?: number, padding?: number) => void;
        };
        fg?.zoomToFit?.(400, 50);
      }
    }, 500);

    return () => clearTimeout(t1);
  }, [apiData, configureAndReheat, accountIdParam]);

  const handleNodeClick = useCallback(
    (node: NodeObject) => {
      const graphNode = node as GraphNode;
      setSelectedNode(graphNode);
      setSelectedLink(null);
      // Update URL with selected account
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("platform", dataSource);
        next.set("account_id", graphNode.id);
        return next;
      });
    },
    [dataSource, setSearchParams]
  );

  const handleNodeHover = useCallback((node: NodeObject | null) => {
    setHoveredNode(node as GraphNode | null);
    if (containerRef.current) {
      containerRef.current.style.cursor = node ? "pointer" : "grab";
    }
  }, []);

  const handleLinkClick = useCallback(
    (link: LinkObject) => {
      const sourceId =
        typeof link.source === "string" ? link.source : (link.source as GraphNode)?.id;
      const targetId =
        typeof link.target === "string" ? link.target : (link.target as GraphNode)?.id;
      const originalLink = graphData.links.find(
        (l) =>
          ((l.source as string) === sourceId || (l.source as GraphNode)?.id === sourceId) &&
          ((l.target as string) === targetId || (l.target as GraphNode)?.id === targetId)
      );
      if (originalLink) {
        setSelectedLink(originalLink);
      } else {
        setSelectedLink(link as GraphLink);
      }
      setSelectedNode(null);
    },
    [graphData.links]
  );

  const handleZoomIn = () => {
    const fg = graphRef.current as unknown as {zoom: (k: number, ms?: number) => void};
    if (fg?.zoom) fg.zoom(1.5, 300);
  };

  const handleZoomOut = () => {
    const fg = graphRef.current as unknown as {zoom: (k: number, ms?: number) => void};
    if (fg?.zoom) fg.zoom(0.67, 300);
  };

  const handleFitView = () => {
    const fg = graphRef.current as unknown as {zoomToFit: (ms?: number) => void};
    if (fg?.zoomToFit) fg.zoomToFit(400);
  };

  const navigateToNode = useCallback((node: GraphNode) => {
    setSelectedNode(node);
    setSelectedLink(null);
    const fg = graphRef.current as unknown as {
      centerAt: (x: number, y: number, ms?: number) => void;
      zoom: (k: number, ms?: number) => void;
    };
    const nodeWithCoords = node as NodeWithCoords;
    if (fg?.centerAt && nodeWithCoords.x !== undefined && nodeWithCoords.y !== undefined) {
      fg.centerAt(nodeWithCoords.x, nodeWithCoords.y, 400);
      fg.zoom(2, 400);
    }
  }, []);

  const nodeCanvasObject = useCallback(
    (node: NodeObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const typedNode = node as NodeWithCoords;
      const {x, y, username, color, size, profile_image} = typedNode;
      if (x === undefined || y === undefined) return;

      const nodeSize = size || 8;
      const isHighlighted = selectedNode?.id === typedNode.id || hoveredNode?.id === typedNode.id;

      if (isHighlighted) {
        ctx.beginPath();
        ctx.arc(x, y, nodeSize + 4, 0, 2 * Math.PI);
        ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
        ctx.fill();
      }

      let imageDrawn = false;
      if (profile_image) {
        if (imgCache.has(profile_image)) {
          const img = imgCache.get(profile_image)!;
          if (img.complete && img.naturalHeight !== 0) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, y, nodeSize, 0, 2 * Math.PI);
            ctx.clip();
            try {
              ctx.drawImage(img, x - nodeSize, y - nodeSize, nodeSize * 2, nodeSize * 2);
              imageDrawn = true;
            } catch {
              // Ignore image draw errors
            }
            ctx.restore();
          }
        } else {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.src = profile_image;
          imgCache.set(profile_image, img);
        }
      }

      if (!imageDrawn) {
        // Draw dark background circle
        ctx.beginPath();
        ctx.arc(x, y, nodeSize, 0, 2 * Math.PI);
        ctx.fillStyle = color || "hsl(0, 0%, 25%)";
        ctx.fill();

        // Draw silhouette (head + shoulders)
        const scale = nodeSize / 12;
        ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
        // Head
        ctx.beginPath();
        ctx.arc(x, y - 2 * scale, 3 * scale, 0, 2 * Math.PI);
        ctx.fill();
        // Shoulders
        ctx.beginPath();
        ctx.arc(x, y + 6 * scale, 5 * scale, Math.PI, 0);
        ctx.fill();
      }

      // Draw border around the node
      ctx.beginPath();
      ctx.arc(x, y, nodeSize, 0, 2 * Math.PI);
      ctx.strokeStyle = isHighlighted ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.3)";
      ctx.lineWidth = isHighlighted ? 2 : 0.5;
      ctx.stroke();

      const fontSize = Math.max(12 / globalScale, 3);
      if (globalScale > 0.5 || nodeSize > 15) {
        ctx.font = `${fontSize}px "DM Sans", sans-serif`;
        ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        const displayName = username.length > 15 ? username.slice(0, 12) + "..." : username;
        ctx.fillText(displayName, x, y + nodeSize + 3);
      }
    },
    [selectedNode, hoveredNode]
  );

  const linkCanvasObject = useCallback(
    (link: LinkObject, ctx: CanvasRenderingContext2D) => {
      const typedLink = link as LinkObject & {source: NodeWithCoords; target: NodeWithCoords};
      const {source, target} = typedLink;

      if (!source.x || !source.y || !target.x || !target.y) return;

      const isHighlighted =
        selectedNode?.id === source.id ||
        selectedNode?.id === target.id ||
        hoveredNode?.id === source.id ||
        hoveredNode?.id === target.id ||
        selectedLink === link;

      const gradient = ctx.createLinearGradient(source.x, source.y, target.x, target.y);
      if (isHighlighted) {
        gradient.addColorStop(0, "rgba(255, 255, 255, 0.6)");
        gradient.addColorStop(1, "rgba(200, 200, 200, 0.3)");
      } else {
        gradient.addColorStop(0, "rgba(120, 120, 120, 0.3)");
        gradient.addColorStop(1, "rgba(80, 80, 80, 0.15)");
      }

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = isHighlighted ? 2 : 1;
      ctx.stroke();

      const arrowSize = isHighlighted ? 8 : 6;
      const angle = Math.atan2(target.y - source.y, target.x - source.x);
      // Place arrow in the middle of the edge
      const arrowX = (source.x + target.x) / 2;
      const arrowY = (source.y + target.y) / 2;

      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(
        arrowX - arrowSize * Math.cos(angle - Math.PI / 5),
        arrowY - arrowSize * Math.sin(angle - Math.PI / 5)
      );
      ctx.lineTo(
        arrowX - arrowSize * Math.cos(angle + Math.PI / 5),
        arrowY - arrowSize * Math.sin(angle + Math.PI / 5)
      );
      ctx.closePath();
      ctx.fillStyle = isHighlighted ? "rgba(255, 255, 255, 0.9)" : "rgba(180, 180, 180, 0.7)";
      ctx.fill();
    },
    [selectedNode, hoveredNode, selectedLink]
  );

  const topInfluencers = useMemo(() => {
    return [...graphData.nodes].sort((a, b) => b.influenceCount - a.influenceCount).slice(0, 10);
  }, [graphData]);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  if (isLoading) {
    return (
      <div className="bg-background flex min-h-0 w-full flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          <p className="text-muted-foreground">Loading map of influence...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background flex min-h-0 w-full flex-1 items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load map of influence</p>
          <p className="text-muted-foreground text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  const platformLabel = dataSource === "x" ? "X" : "Instagram";

  return (
    <div className="bg-background text-foreground grid h-full w-full grid-rows-[auto_1fr] overflow-hidden">
      <div className="border-border bg-card/50 border-b px-6 py-4 backdrop-blur-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-foreground text-2xl font-bold tracking-tight">Map of Influences</h1>
            <p className="text-muted-foreground text-sm">
              Based on {dataSource === "x" ? "reply" : "comment"} interactions: if B{" "}
              {dataSource === "x" ? "replies to" : "comments on"} A's post, A influenced B
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ToggleGroup
              type="single"
              value={dataSource}
              onValueChange={(val) => val && handleDataSourceChange(val as DataSource)}
              className="bg-secondary/50 rounded-md p-1"
            >
              <ToggleGroupItem
                value="instagram"
                aria-label="Instagram"
                className="cursor-pointer px-3"
              >
                <SiInstagram className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="x" aria-label="X" className="cursor-pointer px-3">
                <SiX className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-secondary/50 border-border placeholder:text-muted-foreground w-64 pl-9"
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleZoomIn}
                    className="border-border bg-secondary/50 cursor-pointer"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom In</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleZoomOut}
                    className="border-border bg-secondary/50 cursor-pointer"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom Out</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleFitView}
                    className="border-border bg-secondary/50 cursor-pointer"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Fit to View</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <div className="grid min-h-0 grid-cols-[1fr_24rem] overflow-hidden">
        <div ref={containerRef} className="bg-muted/10 relative min-h-0 min-w-0">
          {dimensions.width > 0 && dimensions.height > 0 ? (
            <ForceGraph2D
              key={dataSource}
              ref={graphRef as React.MutableRefObject<never>}
              width={dimensions.width}
              height={dimensions.height}
              graphData={filteredData}
              nodeId="id"
              nodeCanvasObject={nodeCanvasObject}
              nodePointerAreaPaint={(node, color, ctx) => {
                const typedNode = node as NodeWithCoords;
                if (typedNode.x === undefined || typedNode.y === undefined) return;
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(typedNode.x, typedNode.y, typedNode.size || 8, 0, 2 * Math.PI);
                ctx.fill();
              }}
              linkCanvasObject={linkCanvasObject}
              linkDirectionalArrowLength={0}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              onLinkClick={handleLinkClick}
              warmupTicks={100}
              cooldownTicks={150}
              d3AlphaDecay={0.03}
              d3VelocityDecay={0.4}
              backgroundColor="transparent"
              enableNodeDrag={true}
              enableZoomInteraction={true}
              enablePanInteraction={true}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2">
              <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
              <p className="text-muted-foreground font-mono text-xs">
                Initializing graph... ({dimensions.width}x{dimensions.height})
              </p>
            </div>
          )}
        </div>

        <div className="bg-card/70 border-border flex flex-col overflow-hidden border-l backdrop-blur-sm">
          <ScrollArea className="h-full flex-1 p-4">
            <div className="bg-secondary/50 mb-6 rounded-lg p-4">
              <div className="mb-3 flex items-center gap-2">
                <h3 className="text-foreground font-medium">{platformLabel} Database Stats</h3>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground h-4 w-4 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p>
                        When someone {dataSource === "x" ? "replies to" : "comments on"} a post, the
                        post author is considered to have influenced them.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-muted/50 rounded-md p-2">
                  <div className="text-foreground text-2xl font-bold">
                    {filteredData.nodes.length}
                  </div>
                  <div className="text-muted-foreground">Accounts</div>
                </div>
                <div className="bg-muted/50 rounded-md p-2">
                  <div className="text-foreground text-2xl font-bold">
                    {filteredData.links.length}
                  </div>
                  <div className="text-muted-foreground">Influences</div>
                </div>
              </div>
            </div>

            {selectedLink && (
              <div className="border-border bg-secondary/30 mb-6 rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-foreground font-medium">Interaction Details</h3>
                  <button
                    onClick={() => setSelectedLink(null)}
                    className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-muted-foreground mb-4 text-sm">
                  <a
                    href={getProfileUrl(
                      typeof selectedLink.source === "string"
                        ? selectedLink.source
                        : (selectedLink.source as GraphNode).username,
                      dataSource
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground cursor-pointer font-medium hover:underline"
                  >
                    @
                    {typeof selectedLink.source === "string"
                      ? selectedLink.source
                      : (selectedLink.source as GraphNode).username}
                  </a>{" "}
                  influenced{" "}
                  <a
                    href={getProfileUrl(
                      typeof selectedLink.target === "string"
                        ? selectedLink.target
                        : (selectedLink.target as GraphNode).username,
                      dataSource
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground cursor-pointer font-medium hover:underline"
                  >
                    @
                    {typeof selectedLink.target === "string"
                      ? selectedLink.target
                      : (selectedLink.target as GraphNode).username}
                  </a>
                  <span className="text-muted-foreground ml-1">
                    ({selectedLink.value} {dataSource === "x" ? "repl" : "comment"}
                    {selectedLink.value !== 1
                      ? dataSource === "x"
                        ? "ies"
                        : "s"
                      : dataSource === "x"
                        ? "y"
                        : ""}
                    )
                  </span>
                </div>
                <div className="space-y-3">
                  <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                    {dataSource === "x" ? "Replies" : "Comments"} showing influence
                  </p>
                  {selectedLink.comments?.slice(0, 5).map((comment, idx) => (
                    <div key={idx} className="bg-muted/30 space-y-2 rounded-md p-3">
                      {comment.parentUrl && (
                        <div className="border-border border-l-2 pl-2">
                          <span className="text-muted-foreground text-xs">
                            {dataSource === "x" ? "Replying to" : "Commenting on"}:
                          </span>
                          <a
                            href={comment.parentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-foreground ml-1 inline-flex cursor-pointer items-center gap-1 text-xs transition-colors"
                          >
                            Original post
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                      <p className="text-foreground text-sm leading-snug">
                        "{truncateText(comment.comment, 150)}"
                      </p>
                      <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                        <span>{formatDate(comment.date)}</span>
                        {comment.likes > 0 && (
                          <span className="flex items-center gap-1">❤️ {comment.likes}</span>
                        )}
                      </div>
                      {comment.postUrl && (
                        <a
                          href={comment.postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center gap-1 text-xs transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View {dataSource === "x" ? "Reply" : "Comment"} on {platformLabel}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedNode && (
              <div className="border-border bg-secondary/30 mb-6 rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-foreground font-medium">Selected Account</h3>
                  <button
                    onClick={() => setSelectedNode(null)}
                    className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative h-12 w-12 flex-shrink-0">
                    {selectedNode.profile_image ? (
                      <img
                        src={selectedNode.profile_image}
                        alt={selectedNode.username}
                        className="border-border h-12 w-12 rounded-full border object-cover"
                      />
                    ) : (
                      <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
                        <User className="text-muted-foreground h-6 w-6" />
                      </div>
                    )}
                    <div
                      className={`absolute right-0 bottom-0 flex h-4 w-4 translate-x-0.5 translate-y-0.5 items-center justify-center rounded-full ring-2 ring-white dark:ring-zinc-900 ${dataSource === "x" ? "bg-black" : "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400"}`}
                    >
                      {dataSource === "x" ? (
                        <SiX className="h-2.5 w-2.5 text-white" />
                      ) : (
                        <SiInstagram className="h-2.5 w-2.5 text-white" />
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <a
                        href={getProfileUrl(selectedNode.username, dataSource)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground cursor-pointer font-medium hover:underline"
                      >
                        @{selectedNode.username}
                      </a>
                    </div>
                    {(selectedNode.followers != null || selectedNode.following != null) && (
                      <div className="text-muted-foreground mt-0.5 flex gap-2 text-xs">
                        {selectedNode.followers != null && (
                          <span>
                            <span className="text-foreground font-medium">
                              {selectedNode.followers >= 1000000
                                ? `${(selectedNode.followers / 1000000).toFixed(1)}M`
                                : selectedNode.followers >= 1000
                                  ? `${(selectedNode.followers / 1000).toFixed(1)}K`
                                  : selectedNode.followers}
                            </span>{" "}
                            followers
                          </span>
                        )}
                        {selectedNode.following != null && (
                          <span>
                            <span className="text-foreground font-medium">
                              {selectedNode.following >= 1000
                                ? `${(selectedNode.following / 1000).toFixed(1)}K`
                                : selectedNode.following}
                            </span>{" "}
                            following
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {selectedNode.biography && (
                  <p className="text-muted-foreground mt-2 line-clamp-2 text-xs">
                    {selectedNode.biography}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <a
                    href={getProfileUrl(selectedNode.username, dataSource)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground hover:bg-muted inline-flex h-6 cursor-pointer items-center gap-1 rounded-md px-2 text-xs transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View Profile
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isSelectedNodeFavorited || createFavoriteMutation.isPending}
                    onClick={handleAddToFavorites}
                    className="h-6 gap-1 px-2 text-xs"
                  >
                    <Star
                      className={`h-3 w-3 ${isSelectedNodeFavorited ? "fill-yellow-500 text-yellow-500" : ""}`}
                    />
                    {isSelectedNodeFavorited ? "Favorited" : "Add to Favorites"}
                  </Button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-muted/50 rounded-md p-2 text-center">
                    <div className="text-foreground text-lg font-bold">
                      {selectedNode.influenceCount}
                    </div>
                    <div className="text-muted-foreground text-xs">People Influenced</div>
                  </div>
                  <div className="bg-muted/50 rounded-md p-2 text-center">
                    <div className="text-foreground text-lg font-bold">
                      {selectedNode.influencedBy}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      Influenced via {dataSource === "x" ? "Reply" : "Comment"}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wider uppercase">
                    Narratives
                  </p>
                  <div className="space-y-2">
                    <div className="bg-muted/30 flex items-center gap-2 rounded-md p-2">
                      <div className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
                      <span className="text-foreground flex-1 text-sm">Pro-crypto regulation</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled
                        className="h-7 gap-1 bg-violet-500/10 px-2 text-violet-600 opacity-50 dark:text-violet-400"
                      >
                        <Wand2 className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium">Influence</span>
                      </Button>
                    </div>
                    <div className="bg-muted/30 flex items-center gap-2 rounded-md p-2">
                      <div className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
                      <span className="text-foreground flex-1 text-sm">
                        Tech innovation skeptic
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled
                        className="h-7 gap-1 bg-violet-500/10 px-2 text-violet-600 opacity-50 dark:text-violet-400"
                      >
                        <Wand2 className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium">Influence</span>
                      </Button>
                    </div>
                    <div className="bg-muted/30 flex items-center gap-2 rounded-md p-2">
                      <div className="h-2 w-2 shrink-0 rounded-full bg-zinc-400" />
                      <span className="text-foreground flex-1 text-sm">
                        Market volatility neutral
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled
                        className="h-7 gap-1 bg-violet-500/10 px-2 text-violet-600 opacity-50 dark:text-violet-400"
                      >
                        <Wand2 className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium">Influence</span>
                      </Button>
                    </div>
                  </div>
                </div>

                {selectedNodeLinks.length > 0 && (
                  <div className="mt-4">
                    <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wider uppercase">
                      Influence via {dataSource === "x" ? "Replies" : "Comments"} (
                      {selectedNodeLinks.length})
                    </p>
                    <div className="space-y-2">
                      {selectedNodeLinks.slice(0, 5).map((link, idx) => {
                        const sourceId =
                          typeof link.source === "string"
                            ? link.source
                            : (link.source as GraphNode).id;
                        const targetId =
                          typeof link.target === "string"
                            ? link.target
                            : (link.target as GraphNode).id;
                        const isOutgoing = sourceId === selectedNode.id;
                        const otherUser = isOutgoing ? targetId : sourceId;
                        const commentSample = link.comments?.[0];

                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              setSelectedLink(link);
                              setSelectedNode(null);
                            }}
                            className="bg-muted/30 hover:bg-muted/50 block w-full cursor-pointer rounded-md p-2 text-left transition-colors"
                          >
                            <div className="text-foreground text-sm">
                              {isOutgoing ? "Influenced" : "Influenced by"}{" "}
                              <span className="font-medium">@{otherUser}</span>
                            </div>
                            {commentSample && (
                              <p className="text-muted-foreground mt-1 text-xs">
                                "{truncateText(commentSample.comment, 50)}"
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-secondary/50 rounded-lg p-4">
              <h3 className="text-foreground mb-3 font-medium">Top Influencers</h3>
              <div className="space-y-2">
                {topInfluencers.map((node) => (
                  <div
                    key={node.id}
                    onClick={() => navigateToNode(node)}
                    className="hover:bg-muted/50 flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors"
                  >
                    <div className="relative h-6 w-6 flex-shrink-0">
                      {node.profile_image ? (
                        <img
                          src={node.profile_image}
                          alt={node.username}
                          className="border-border h-6 w-6 rounded-full border object-cover"
                        />
                      ) : (
                        <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                          <User className="text-muted-foreground h-3.5 w-3.5" />
                        </div>
                      )}
                      <div
                        className={`absolute right-0 bottom-0 flex h-3 w-3 translate-x-0.5 translate-y-0.5 items-center justify-center rounded-full ring-1 ring-white dark:ring-zinc-900 ${dataSource === "x" ? "bg-black" : "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400"}`}
                      >
                        {dataSource === "x" ? (
                          <SiX className="h-1.5 w-1.5 text-white" />
                        ) : (
                          <SiInstagram className="h-1.5 w-1.5 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="text-foreground truncate text-sm">@{node.username}</div>
                      <div className="text-muted-foreground text-xs">
                        {node.influenceCount} people influenced
                      </div>
                    </div>
                    <a
                      href={getProfileUrl(node.username, dataSource)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-foreground flex-shrink-0 cursor-pointer transition-colors"
                      title={`View on ${platformLabel}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-muted/30 mt-6 rounded-lg p-4">
              <h3 className="text-foreground mb-3 text-sm font-medium">How Influence Works</h3>
              <div className="text-muted-foreground space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-3 w-3" />
                  <span>
                    When B {dataSource === "x" ? "replies to" : "comments on"} A's post → A
                    influenced B
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-gradient-to-r from-neutral-400 to-neutral-600" />
                  <span>Larger nodes = More people influenced</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-0.5 w-6 bg-gradient-to-r from-neutral-400 to-neutral-600" />
                  <span>Arrow direction = Influence flow</span>
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
